import { Callout } from "@moneymap/ui";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useApi } from "../api/ApiProvider";
import { useAuth } from "../auth/AuthProvider";
import "./app-shell-iternest.css";

type ShellIconName =
  | "activity"
  | "bell"
  | "coverage"
  | "goals"
  | "home"
  | "logout"
  | "more"
  | "plan"
  | "profile"
  | "refresh"
  | "spark";

const primaryNavigation: Array<{ to: string; label: string; icon: ShellIconName }> = [
  { to: "/home", label: "Home", icon: "home" },
  { to: "/activity", label: "Activity", icon: "activity" },
  { to: "/plan", label: "Plan", icon: "plan" },
  { to: "/goals", label: "Goals", icon: "goals" },
  { to: "/setup", label: "Setup", icon: "more" },
];

const secondaryNavigation: Array<{ to: string; label: string; icon: ShellIconName }> = [
  { to: "/coverage", label: "Coverage", icon: "coverage" },
  { to: "/spark", label: "Spark", icon: "spark" },
  { to: "/affordability", label: "Can I Afford It?", icon: "plan" },
  { to: "/needs-confirmation", label: "Needs confirmation", icon: "bell" },
  { to: "/settings", label: "Settings", icon: "profile" },
];

function ShellIcon({ name }: { name: ShellIconName }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  switch (name) {
    case "activity":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M8 6h11M8 12h11M8 18h11" />
          <circle cx="4" cy="6" r="1" fill="currentColor" stroke="none" />
          <circle cx="4" cy="12" r="1" fill="currentColor" stroke="none" />
          <circle cx="4" cy="18" r="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case "bell":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
          <path d="M10 21h4" />
        </svg>
      );
    case "coverage":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M12 3 5 6v5c0 4.6 2.8 8 7 10 4.2-2 7-5.4 7-10V6z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "goals":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <circle cx="12" cy="12" r="8" />
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v3M22 12h-3" />
        </svg>
      );
    case "home":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="m3 11 9-8 9 8" />
          <path d="M5 10v10h14V10M9 20v-6h6v6" />
        </svg>
      );
    case "logout":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M10 5H5v14h5" />
          <path d="M13 8l4 4-4 4M17 12H9" />
        </svg>
      );
    case "more":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <circle cx="5" cy="12" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
          <circle cx="19" cy="12" r="1.2" fill="currentColor" stroke="none" />
        </svg>
      );
    case "plan":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M3 13h4l2-6 4 11 2-5h6" />
        </svg>
      );
    case "profile":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <circle cx="12" cy="8" r="4" />
          <path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
        </svg>
      );
    case "refresh":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M20 6v5h-5" />
          <path d="M4 18v-5h5" />
          <path d="M6.1 9a7 7 0 0 1 11.7-2.6L20 8M4 16l2.2 1.6A7 7 0 0 0 17.9 15" />
        </svg>
      );
    case "spark":
      return (
        <svg {...common} aria-hidden="true" focusable="false">
          <path d="M13 2 6 13h6l-1 9 7-12h-6z" />
        </svg>
      );
  }
}

function useOnlineState(): boolean {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener("online", online);
    window.addEventListener("offline", offline);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", offline);
    };
  }, []);
  return isOnline;
}

function Brand() {
  return (
    <NavLink className="brand-mark" to="/home" aria-label="IterNest Money home">
      <img className="brand-mark__logo" src="/iternest-logo.svg" alt="" aria-hidden="true" />
      <span className="brand-mark__name">
        <strong>IterNest</strong>
        <small>Money</small>
      </span>
    </NavLink>
  );
}

export function AppShell() {
  const api = useApi();
  const queryClient = useQueryClient();
  const refreshingQueries = useIsFetching();
  const { email, signOut } = useAuth();
  const isOnline = useOnlineState();
  const briefingQuery = useQuery({
    queryKey: ["briefing"],
    queryFn: () => api.getBriefing(),
    staleTime: 60_000,
  });
  const attentionCount = briefingQuery.data?.data?.needsAttention.length ?? 0;
  const isRefreshing = refreshingQueries > 0;

  const refreshApp = () => {
    void queryClient.invalidateQueries();
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <aside className="desktop-sidebar">
        <Brand />
        <nav aria-label="Primary">
          {primaryNavigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? "side-link is-active" : "side-link")}
            >
              <ShellIcon name={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <nav aria-label="Planning tools" className="secondary-nav">
          <p>Planning tools</p>
          {secondaryNavigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => (isActive ? "side-link is-active" : "side-link")}
            >
              <ShellIcon name={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-account">
          <span>{email}</span>
          <div className="sidebar-account__actions">
            <button
              className="shell-text-button"
              type="button"
              onClick={refreshApp}
              disabled={isRefreshing}
            >
              <ShellIcon name="refresh" />
              <span>{isRefreshing ? "Refreshing…" : "Refresh"}</span>
            </button>
            <button className="shell-text-button" type="button" onClick={() => void signOut()}>
              <ShellIcon name="logout" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      </aside>
      <div className="app-column">
        <header className="mobile-header">
          <Brand />
          <div className="shell-header-actions">
            <button
              className="shell-action-button"
              type="button"
              onClick={refreshApp}
              disabled={isRefreshing}
              aria-label="Refresh app data"
            >
              <ShellIcon name="refresh" />
              <span>{isRefreshing ? "Refreshing…" : "Refresh"}</span>
            </button>
            <NavLink
              className="shell-icon-link"
              to="/needs-confirmation"
              aria-label={
                attentionCount > 0
                  ? `Review ${attentionCount} items needing attention`
                  : "Open notifications"
              }
            >
              <ShellIcon name="bell" />
              {attentionCount > 0 ? (
                <span className="shell-attention-dot" aria-hidden="true" />
              ) : null}
            </NavLink>
            <NavLink
              className="shell-icon-link shell-icon-link--profile"
              to="/settings"
              aria-label="Open profile and settings"
            >
              <ShellIcon name="profile" />
            </NavLink>
            <button
              className="shell-action-button"
              type="button"
              onClick={() => void signOut()}
              aria-label="Log out"
            >
              <ShellIcon name="logout" />
              <span>Log out</span>
            </button>
          </div>
        </header>
        {!isOnline ? (
          <div className="offline-banner">
            <Callout title="You are offline" tone="warning" role="status">
              The last view stays visible when available. Reconnect before saving changes.
            </Callout>
          </div>
        ) : null}
        <main id="main-content" className="page-content" tabIndex={-1}>
          <Outlet />
        </main>
        <nav className="mobile-nav" aria-label="Primary">
          {primaryNavigation.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                isActive ? "mobile-nav__link is-active" : "mobile-nav__link"
              }
            >
              <ShellIcon name={item.icon} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
